import { useState, type CSSProperties, type FormEvent } from 'react';
import { Logo } from './Logo';
import { supabase, isSupabaseConfigured } from '../supabase';

/**
 * Sign-in / sign-up wall shown before the editor (login is required). Email +
 * password via Supabase, plus an optional Google button (works once you enable
 * the Google provider in the Supabase dashboard). If the Supabase env vars
 * aren't set yet, it shows a short setup notice instead of failing silently.
 */
export function AuthScreen({ onBack }: { onBack?: () => void }) {
  const [mode, setMode] = useState<'signin' | 'signup' | 'forgot'>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  if (!isSupabaseConfigured || !supabase) {
    return (
      <div style={page}>
        <div style={card}>
          <Logo size={34} />
          <h1 style={h1}>Sign-in isn&apos;t configured yet</h1>
          <p style={sub}>
            Set <code style={code}>VITE_SUPABASE_URL</code> and{' '}
            <code style={code}>VITE_SUPABASE_ANON_KEY</code> (see <code style={code}>.env.example</code>)
            in your local <code style={code}>.env</code> and in the Cloudflare Pages settings, then
            redeploy.
          </p>
          {onBack && (
            <button style={ghostBtn} onClick={onBack}>
              ← Back
            </button>
          )}
        </div>
      </div>
    );
  }

  // Proven non-null past the guard above; a local const lets the async
  // closures below use it without TS re-widening to `| null`.
  const client = supabase;

  // Only show the Google button once the provider is actually configured in
  // Supabase (Auth → Providers → Google). Otherwise clicking it returns
  // "provider is not enabled". Flip VITE_ENABLE_GOOGLE_AUTH=true to show it.
  const googleEnabled = import.meta.env.VITE_ENABLE_GOOGLE_AUTH === 'true';

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setNotice(null);
    if (mode === 'forgot') {
      if (!email.trim()) {
        setError('Enter your email.');
        return;
      }
      setBusy(true);
      try {
        const { error } = await client.auth.resetPasswordForEmail(email.trim(), {
          redirectTo: window.location.origin + '/',
        });
        if (error) throw error;
        setNotice('If that email has an account, a password-reset link is on its way.');
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not send the reset link.');
      } finally {
        setBusy(false);
      }
      return;
    }
    if (!email.trim() || !password) {
      setError('Enter your email and password.');
      return;
    }
    setBusy(true);
    try {
      if (mode === 'signup') {
        const { error } = await client.auth.signUp({ email: email.trim(), password });
        if (error) throw error;
        // With email confirmation on (Supabase default), there's no session yet.
        setNotice('Check your inbox to confirm your email, then sign in.');
        setMode('signin');
      } else {
        const { error } = await client.auth.signInWithPassword({ email: email.trim(), password });
        if (error) throw error;
        // On success the App's auth listener swaps this screen for the editor.
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const google = async () => {
    setError(null);
    try {
      // Redirect back to the bare origin — do NOT append a hash fragment.
      // Supabase appends the session as `#access_token=...`; adding our own
      // `#app` first would nest it into `#app#access_token=...`, which the
      // client can't parse (a URL has only one fragment), so no session gets
      // established and the user bounces back to this screen. The app's gate
      // already detects `access_token` in the hash and drops into the editor.
      const { error } = await client.auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo: window.location.origin + '/' },
      });
      if (error) throw error;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Google sign-in failed.');
    }
  };

  return (
    <div style={page}>
      <div style={card}>
        <Logo size={34} />
        <h1 style={h1}>
          {mode === 'signin'
            ? 'Sign in to Skematiq'
            : mode === 'signup'
              ? 'Create your account'
              : 'Reset your password'}
        </h1>
        <p style={sub}>
          {mode === 'signin'
            ? 'Welcome back — sign in to open the editor.'
            : mode === 'signup'
              ? 'Free account. It only takes a moment.'
              : 'Enter your email and we’ll send a reset link.'}
        </p>

        {googleEnabled && mode !== 'forgot' && (
          <>
            <button type="button" style={oauthBtn} onClick={google}>
              Continue with Google
            </button>
            <div style={divider}>
              <span style={dividerLine} />
              <span style={dividerText}>or</span>
              <span style={dividerLine} />
            </div>
          </>
        )}

        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <input
            type="email"
            autoComplete="email"
            placeholder="you@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={input}
          />
          {mode !== 'forgot' && (
            <input
              type="password"
              autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={input}
            />
          )}
          {error && <div style={errorBox}>{error}</div>}
          {notice && <div style={noticeBox}>{notice}</div>}
          <button type="submit" disabled={busy} style={{ ...primaryBtn, opacity: busy ? 0.7 : 1 }}>
            {busy
              ? 'Please wait…'
              : mode === 'signin'
                ? 'Sign in'
                : mode === 'signup'
                  ? 'Create account'
                  : 'Send reset link'}
          </button>
          {mode === 'signup' && (
            <p style={legalNote}>
              By creating an account you agree to our{' '}
              <a href="/terms.html" target="_blank" rel="noreferrer" style={legalLink}>
                Terms
              </a>{' '}
              and{' '}
              <a href="/privacy.html" target="_blank" rel="noreferrer" style={legalLink}>
                Privacy Policy
              </a>
              .
            </p>
          )}
        </form>

        <div style={switchRow}>
          {mode === 'signin' && (
            <>
              New here?{' '}
              <button style={linkBtn} onClick={() => { setMode('signup'); setError(null); setNotice(null); }}>
                Create an account
              </button>
              <span style={{ margin: '0 6px', opacity: 0.4 }}>·</span>
              <button style={linkBtn} onClick={() => { setMode('forgot'); setError(null); setNotice(null); }}>
                Forgot password?
              </button>
            </>
          )}
          {mode === 'signup' && (
            <>
              Already have an account?{' '}
              <button style={linkBtn} onClick={() => { setMode('signin'); setError(null); setNotice(null); }}>
                Sign in
              </button>
            </>
          )}
          {mode === 'forgot' && (
            <button style={linkBtn} onClick={() => { setMode('signin'); setError(null); setNotice(null); }}>
              ← Back to sign in
            </button>
          )}
        </div>

        {onBack && (
          <button style={ghostBtn} onClick={onBack}>
            ← Back to home
          </button>
        )}
      </div>
    </div>
  );
}

const page: CSSProperties = {
  minHeight: '100vh',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 24,
  background: 'radial-gradient(1200px 600px at 50% -10%, #172234 0%, var(--bg, #0d1117) 60%)',
  color: 'var(--text)',
  fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif',
  boxSizing: 'border-box',
};
const card: CSSProperties = {
  width: '100%',
  maxWidth: 380,
  background: 'var(--panel)',
  border: '1px solid var(--panel-border)',
  borderRadius: 'var(--radius)',
  padding: 26,
  boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
};
const h1: CSSProperties = { fontSize: 20, fontWeight: 800, margin: '6px 0 0' };
const sub: CSSProperties = { fontSize: 13, color: 'var(--text-muted)', margin: '0 0 6px', lineHeight: 1.5 };
const input: CSSProperties = {
  border: '1px solid var(--panel-border)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--panel-alt, var(--panel))',
  color: 'var(--text)',
  padding: '10px 12px',
  fontSize: 14,
  outline: 'none',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
};
const primaryBtn: CSSProperties = {
  border: 'none',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--accent)',
  color: '#fff',
  fontSize: 14,
  fontWeight: 700,
  padding: '10px 14px',
  cursor: 'pointer',
  marginTop: 2,
};
const oauthBtn: CSSProperties = {
  border: '1px solid var(--panel-border)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--panel-alt, var(--panel))',
  color: 'var(--text)',
  fontSize: 14,
  fontWeight: 700,
  padding: '10px 14px',
  cursor: 'pointer',
};
const ghostBtn: CSSProperties = {
  border: 'none',
  background: 'transparent',
  color: 'var(--text-muted)',
  fontSize: 12.5,
  fontWeight: 600,
  padding: '6px 0 0',
  cursor: 'pointer',
  alignSelf: 'center',
};
const linkBtn: CSSProperties = {
  border: 'none',
  background: 'transparent',
  color: 'var(--accent)',
  fontSize: 12.5,
  fontWeight: 700,
  padding: 0,
  cursor: 'pointer',
};
const switchRow: CSSProperties = { fontSize: 12.5, color: 'var(--text-muted)', textAlign: 'center' };
const legalNote: CSSProperties = {
  fontSize: 11,
  color: 'var(--text-dim)',
  lineHeight: 1.5,
  textAlign: 'center',
  margin: '2px 0 0',
};
const legalLink: CSSProperties = { color: 'var(--text-muted)', textDecoration: 'underline' };
const divider: CSSProperties = { display: 'flex', alignItems: 'center', gap: 10, margin: '2px 0' };
const dividerLine: CSSProperties = { flex: 1, height: 1, background: 'var(--panel-border)' };
const dividerText: CSSProperties = { fontSize: 11, color: 'var(--text-dim)', fontWeight: 600 };
const errorBox: CSSProperties = {
  fontSize: 12.5,
  color: '#fecaca',
  background: 'rgba(220,38,38,0.12)',
  border: '1px solid rgba(220,38,38,0.4)',
  borderRadius: 'var(--radius-sm)',
  padding: '8px 10px',
  lineHeight: 1.4,
};
const noticeBox: CSSProperties = {
  fontSize: 12.5,
  color: 'var(--text)',
  background: 'var(--accent-soft)',
  border: '1px solid var(--accent-border)',
  borderRadius: 'var(--radius-sm)',
  padding: '8px 10px',
  lineHeight: 1.4,
};
const code: CSSProperties = {
  fontFamily: 'ui-monospace, monospace',
  fontSize: 11.5,
  background: 'var(--panel-alt, var(--panel))',
  border: '1px solid var(--panel-border)',
  borderRadius: 4,
  padding: '1px 4px',
};
